/**
 * Rook Components — Shoppable Video Carousel
 * Web Component: <rk-shoppable-video-carousel>
 *
 * Video inline com autoplay mudo via IntersectionObserver.
 * Clique no video abre modal fullscreen estilo Stories com
 * carousel, controles de mute, navegacao por tap/swipe/teclado,
 * e card de produto com backdrop-blur na base.
 */
if (!customElements.get('rk-shoppable-video-carousel')) {
  class RkShoppableVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
      this._items = [];
      this._onPlayClick = this._onPlayClick.bind(this);
      this._onMuteClick = this._onMuteClick.bind(this);

      // Modal state
      this._modalEl = null;
      this._modalDiv = null;
      this._stage = null;
      this._modalCarousel = null;
      this._swiper = null;
      this._modalVideos = [];
      this._activeIndex = -1;
      this._isMuted = true;
      this._productCard = null;

      this._onCarouselInit = this._onCarouselInit.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this._items = [...this.querySelectorAll('.rk-sv__item')];

      this._items.forEach((item) => {
        const playBtn = item.querySelector('.rk-sv__play-btn');
        const muteBtn = item.querySelector('.rk-sv__mute-btn');

        if (playBtn) playBtn.addEventListener('click', this._onPlayClick);
        if (muteBtn) muteBtn.addEventListener('click', this._onMuteClick);

        // Mark items with video as playing immediately (autoplay is on by default)
        const video = item.querySelector('video');
        if (video) {
          video.muted = true;
          item.classList.add('is-muted');
          video.play().then(() => {
            item.classList.add('is-playing');
          }).catch(() => {});
        }
      });

      this._setupVisibility();
      this._setupModal();
      this._bindVideoClicks();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      this._items.forEach((item) => {
        const playBtn = item.querySelector('.rk-sv__play-btn');
        const muteBtn = item.querySelector('.rk-sv__mute-btn');
        if (playBtn) playBtn.removeEventListener('click', this._onPlayClick);
        if (muteBtn) muteBtn.removeEventListener('click', this._onMuteClick);
      });

      document.removeEventListener('keydown', this._onKeyDown);
      if (this._modalEl?.parentNode === document.body) {
        document.body.removeChild(this._modalEl);
      }
    }

    /* ----------------------------------------------------------------
       Inline video — IntersectionObserver
       ---------------------------------------------------------------- */

    _setupVisibility() {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const item = entry.target.closest('.rk-sv__item');
            const video = entry.target;

            if (!item || !video || video.tagName !== 'VIDEO') return;

            if (entry.isIntersecting) {
              video.play().then(() => {
                item.classList.add('is-playing');
              }).catch(() => {});
            } else {
              video.pause();
              item.classList.remove('is-playing');
            }
          });
        },
        { threshold: 0.3 }
      );

      this._items.forEach((item) => {
        const video = item.querySelector('video');
        if (video) {
          this._observer.observe(video);
        }
      });
    }

    /* ----------------------------------------------------------------
       Inline video — Play / Mute buttons
       ---------------------------------------------------------------- */

    _onPlayClick(e) {
      e.stopPropagation();
      const item = e.currentTarget.closest('.rk-sv__item');
      if (!item) return;

      const video = item.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.play().then(() => {
        item.classList.add('is-playing', 'is-muted');
      }).catch(() => {});
    }

    _onMuteClick(e) {
      e.stopPropagation();
      const item = e.currentTarget.closest('.rk-sv__item');
      if (!item) return;

      const video = item.querySelector('video');
      if (!video) return;

      if (video.muted) {
        this._muteAllInline();
        video.muted = false;
        item.classList.remove('is-muted');
      } else {
        video.muted = true;
        item.classList.add('is-muted');
      }
    }

    _muteAllInline() {
      this._items.forEach((item) => {
        const video = item.querySelector('video');
        if (video) {
          video.muted = true;
          item.classList.add('is-muted');
        }
      });
    }

    /* ----------------------------------------------------------------
       Video click → open modal
       ---------------------------------------------------------------- */

    _bindVideoClicks() {
      this._items.forEach((item, index) => {
        const wrapper = item.querySelector('.rk-sv__video-wrapper');
        if (!wrapper) return;

        wrapper.addEventListener('click', (e) => {
          // Don't open modal if clicking play or mute buttons,
          // nor the product card (o clique lá deve ir direto pro produto,
          // sem o modal piscar antes da navegação)
          if (
            e.target.closest('.rk-sv__play-btn') ||
            e.target.closest('.rk-sv__mute-btn') ||
            e.target.closest('.rk-sv__card')
          )
            return;
          this._openModal(index);
        });

        // Make the wrapper appear clickable
        wrapper.style.cursor = 'pointer';
      });
    }

    /* ================================================================
       MODAL
       ================================================================ */

    _setupModal() {
      this._modalEl = this.querySelector('rk-modal-element');
      if (!this._modalEl) return;

      // Move modal to body to escape stacking contexts
      document.body.appendChild(this._modalEl);

      this._modalDiv = this._modalEl.querySelector('.rk-modal');
      this._stage = this._modalEl.querySelector('.rk-sv-modal__stage');
      this._modalCarousel = this._modalEl.querySelector('rk-carousel-element');
      this._productCard = this._modalEl.querySelector('.rk-sv-modal__product');

      this._buildModalSlides();
      this._bindModalControls();
      this._initModalCarousel();
    }

    /* ----------------------------------------------------------------
       Build modal slides from inline items
       ---------------------------------------------------------------- */

    _buildModalSlides() {
      if (!this._modalCarousel) return;

      const swiperWrapper = this._modalCarousel.querySelector('.swiper-wrapper');
      if (!swiperWrapper) return;

      this._items.forEach((item, index) => {
        const video = item.querySelector('video');
        if (!video) return;

        const slide = document.createElement('div');
        slide.className = 'swiper-slide rk-carousel__slide';
        slide.dataset.slideIndex = index;

        const modalVideo = document.createElement('video');
        modalVideo.className = 'rk-sv-modal__video';
        modalVideo.setAttribute('playsinline', '');
        modalVideo.setAttribute('muted', '');
        modalVideo.setAttribute('loop', '');
        modalVideo.setAttribute('preload', 'none');

        // Copy sources from inline video
        const sources = video.querySelectorAll('source');
        sources.forEach((src) => {
          const newSrc = document.createElement('source');
          newSrc.src = src.src;
          newSrc.type = src.type;
          modalVideo.appendChild(newSrc);
        });

        // If video has direct src attribute
        if (!sources.length && video.src) {
          modalVideo.src = video.src;
        }

        slide.appendChild(modalVideo);
        swiperWrapper.appendChild(slide);
      });

      this._modalVideos = [...this._modalCarousel.querySelectorAll('.rk-sv-modal__video')];
    }

    /* ----------------------------------------------------------------
       Modal carousel init
       ---------------------------------------------------------------- */

    _initModalCarousel() {
      if (!this._modalCarousel) return;

      if (this._modalCarousel.swiper) {
        this._onModalCarouselReady(this._modalCarousel.swiper);
      }
      this._modalCarousel.addEventListener('rk:carousel:init', this._onCarouselInit);
    }

    _onCarouselInit(e) {
      if (e.detail?.swiper && !this._swiper) {
        this._onModalCarouselReady(e.detail.swiper);
      }
    }

    _onModalCarouselReady(swiper) {
      this._swiper = swiper;

      this._swiper.on('slideChange', () => {
        this._activateSlide(this._swiper.activeIndex);
      });
    }

    /* ----------------------------------------------------------------
       Open / Close modal
       ---------------------------------------------------------------- */

    _openModal(index) {
      this._pendingIndex = index;

      if (this._modalDiv) {
        this._modalDiv.classList.add('rk-modal--active');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this._onKeyDown);

        // Pause all inline videos
        this._items.forEach((item) => {
          const v = item.querySelector('video');
          if (v) v.pause();
        });

        this._onModalOpened();
      }
    }

    _onModalOpened() {
      const index = this._pendingIndex || 0;
      this._pendingIndex = null;

      const initSlides = () => {
        if (this._swiper) {
          this._swiper.update();
          this._swiper.slideTo(index, 0);
          this._swiper.update();
        }
        this._activateSlide(index);
      };

      const onTransitionEnd = (e) => {
        if (e.target !== this._modalDiv) return;
        this._modalDiv.removeEventListener('transitionend', onTransitionEnd);
        initSlides();
      };

      this._modalDiv.addEventListener('transitionend', onTransitionEnd);

      // Fallback if no transition fires
      setTimeout(() => {
        this._modalDiv.removeEventListener('transitionend', onTransitionEnd);
        initSlides();
      }, 350);
    }

    _closeModal() {
      this._deactivateCurrentSlide();
      this._activeIndex = -1;

      document.removeEventListener('keydown', this._onKeyDown);

      if (this._modalDiv) {
        this._modalDiv.classList.remove('rk-modal--active');
        document.body.style.overflow = '';
      }

      // Resume inline videos that are visible
      this._items.forEach((item) => {
        const v = item.querySelector('video');
        if (v) {
          v.play().then(() => {
            item.classList.add('is-playing');
          }).catch(() => {});
        }
      });
    }

    /* ----------------------------------------------------------------
       Slide activation / video control
       ---------------------------------------------------------------- */

    _activateSlide(index) {
      if (index === this._activeIndex) return;

      this._deactivateCurrentSlide();
      this._activeIndex = index;

      const video = this._modalVideos[index];
      if (!video) return;

      video.muted = this._isMuted;
      video.currentTime = 0;

      const tryPlay = () => {
        video.play().catch(() => {
          video.muted = true;
          this._isMuted = true;
          this._syncMuteUI();
          video.play().catch(() => {});
        });
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.load();
        video.addEventListener('canplay', tryPlay, { once: true });
      }

      // Update product card
      this._updateProductCard(index);
    }

    _deactivateCurrentSlide() {
      if (this._activeIndex < 0) return;

      const video = this._modalVideos[this._activeIndex];
      if (video) video.pause();
    }

    /* ----------------------------------------------------------------
       Product card overlay
       ---------------------------------------------------------------- */

    _updateProductCard(index) {
      if (!this._productCard) return;

      const item = this._items[index];
      if (!item) return;

      const url = item.dataset.productUrl;
      const title = item.dataset.productTitle;
      const price = item.dataset.productPrice;
      const image = item.dataset.productImage;
      const imageAlt = item.dataset.productImageAlt;

      if (!url) {
        this._productCard.style.display = 'none';
        return;
      }

      this._productCard.href = url;
      this._productCard.querySelector('.rk-sv-modal__product-title').textContent = title || '';
      this._productCard.querySelector('.rk-sv-modal__product-price').textContent = price || '';

      // lativ: tamanhos para compra direta no story
      const sizesEl = this._productCard.querySelector('[data-modal-sizes]');
      if (sizesEl) {
        sizesEl.innerHTML = '';
        let variants = [];
        try {
          variants = JSON.parse(item.dataset.productVariants || '[]');
        } catch (err) {
          variants = [];
        }
        const hasRealVariants =
          variants.length > 1 || (variants.length === 1 && variants[0].title !== 'Default Title');
        if (hasRealVariants) {
          variants.forEach((v) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'rk-sv-modal__size';
            b.textContent = v.title;
            b.dataset.variantId = v.id;
            if (!v.available) b.disabled = true;
            sizesEl.appendChild(b);
          });
          sizesEl.style.display = '';
        } else {
          sizesEl.style.display = 'none';
        }
        if (!this._sizesBound) {
          this._sizesBound = true;
          sizesEl.addEventListener('click', async (e) => {
            const btn = e.target.closest('.rk-sv-modal__size');
            if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
            e.preventDefault();
            e.stopPropagation();
            btn.classList.add('is-loading');
            try {
              const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              const res = await fetch(root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: 1 }] }),
              });
              if (!res.ok) throw new Error('add falhou: ' + res.status);
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: { data: { source: 'rk-sv-story' } },
                })
              );
              btn.classList.remove('is-loading');
              btn.classList.add('is-added');
              setTimeout(() => btn.classList.remove('is-added'), 1600);
            } catch (err) {
              console.error('[rk-sv story add]', err);
              btn.classList.remove('is-loading');
            }
          });
        }
      }

      const imgEl = this._productCard.querySelector('.rk-sv-modal__product-img');
      if (image) {
        imgEl.src = image;
        imgEl.alt = imageAlt || title || '';
        imgEl.style.display = '';
      } else {
        imgEl.style.display = 'none';
      }

      this._productCard.style.display = '';
    }

    /* ----------------------------------------------------------------
       Modal controls
       ---------------------------------------------------------------- */

    _bindModalControls() {
      const closeBtn = this._modalEl?.querySelector('.rk-sv-modal__close');
      if (closeBtn) closeBtn.addEventListener('click', () => this._closeModal());

      const overlay = this._modalEl?.querySelector('.rk-modal__overlay');
      if (overlay) overlay.addEventListener('click', () => this._closeModal());

      const muteBtn = this._modalEl?.querySelector('.rk-sv-modal__mute');
      if (muteBtn) muteBtn.addEventListener('click', () => this._toggleMute());

      const tapPrev = this._modalEl?.querySelector('[data-tap="prev"]');
      const tapNext = this._modalEl?.querySelector('[data-tap="next"]');
      if (tapPrev) tapPrev.addEventListener('click', () => this._goPrev());
      if (tapNext) tapNext.addEventListener('click', () => this._goNext());
    }

    _toggleMute() {
      this._isMuted = !this._isMuted;
      const video = this._modalVideos[this._activeIndex];
      if (video) video.muted = this._isMuted;
      this._syncMuteUI();
    }

    _syncMuteUI() {
      if (this._stage) {
        this._stage.classList.toggle('is-unmuted', !this._isMuted);
      }
    }

    _goPrev() {
      if (this._activeIndex <= 0) return;
      if (this._swiper) this._swiper.slidePrev();
    }

    _goNext() {
      if (this._activeIndex >= this._modalVideos.length - 1) {
        this._closeModal();
        return;
      }
      if (this._swiper) this._swiper.slideNext();
    }

    /* ----------------------------------------------------------------
       Keyboard
       ---------------------------------------------------------------- */

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this._closeModal();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._goNext();
      } else if (e.key === 'm') {
        e.preventDefault();
        this._toggleMute();
      }
    }
  }

  customElements.define('rk-shoppable-video-carousel', RkShoppableVideoCarousel);
}
